/**
 * Timezone Store
 *
 * Manages user timezone preference for analytics display.
 * Falls back to browser timezone when user hasn't set a preference.
 *
 * Timezone is persisted on the server and cached locally.
 * Auto-detection happens on first authenticated visit via Cloudflare's geo-detection.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TimezoneState {
  // User's saved timezone (from server)
  timezone: string | null;

  // Whether we've initialized from the server
  initialized: boolean;

  // Loading state for async operations
  isLoading: boolean;

  // Actions
  setTimezone: (timezone: string | null) => void;
  setInitialized: (initialized: boolean) => void;
  setLoading: (loading: boolean) => void;
}

export const useTimezoneStore = create<TimezoneState>()(
  persist(
    (set) => ({
      timezone: null,
      initialized: false,
      isLoading: false,

      setTimezone: (timezone) => set({ timezone }),
      setInitialized: (initialized) => set({ initialized }),
      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: 'arml-timezone-store',
      // Only persist timezone for offline access
      partialize: (state) => ({ timezone: state.timezone }),
    }
  )
);

/**
 * Validate that a timezone string is a valid IANA timezone.
 */
function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get the effective timezone for display.
 * Returns user's saved timezone or falls back to browser timezone.
 */
export function getEffectiveTimezone(timezone: string | null): string {
  // If timezone is set and valid, use it
  if (timezone && isValidTimezone(timezone)) {
    return timezone;
  }
  // Fallback to browser timezone
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Get the browser's current timezone (IANA format).
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Convert a UTC hour (0-23) to the local hour in the given timezone.
 * Accounts for DST by using a reference date.
 *
 * @param utcHour - Hour in UTC (0-23)
 * @param timezone - IANA timezone identifier
 * @param referenceDate - Optional reference date for DST calculation (defaults to today)
 * @returns Local hour (0-23)
 */
export function convertUtcHourToLocal(
  utcHour: number,
  timezone: string,
  referenceDate: Date = new Date()
): number {
  try {
    // Create a date at the specified UTC hour
    const utcDate = new Date(referenceDate);
    utcDate.setUTCHours(utcHour, 0, 0, 0);

    // Format in the target timezone to get the local hour
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      hour12: false,
    });

    const localHour = parseInt(formatter.format(utcDate), 10);
    return localHour === 24 ? 0 : localHour; // Handle midnight edge case
  } catch {
    // If timezone is invalid, return the UTC hour unchanged
    return utcHour;
  }
}

/**
 * Get the UTC offset string for a timezone (e.g., "UTC-8" or "UTC+5:30").
 * Useful for display purposes.
 *
 * @param timezone - IANA timezone identifier
 * @returns UTC offset string
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const now = new Date();

    // Get the offset in minutes
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });

    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');

    return offsetPart?.value || 'UTC';
  } catch {
    // If timezone is invalid, return UTC
    return 'UTC';
  }
}

/**
 * Format a timezone for display (e.g., "America/Los_Angeles" -> "Los Angeles").
 */
export function formatTimezoneDisplay(timezone: string): string {
  // Remove the region prefix and convert underscores to spaces
  const parts = timezone.split('/');
  const city = parts[parts.length - 1] ?? timezone;
  return city.replace(/_/g, ' ');
}

/**
 * Common timezone options for the settings dropdown.
 * Grouped by region for better UX.
 */
export const TIMEZONE_OPTIONS = [
  // Americas
  { value: 'America/New_York', label: 'New York (Eastern)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Chicago (Central)', region: 'Americas' },
  { value: 'America/Denver', label: 'Denver (Mountain)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (Pacific)', region: 'Americas' },
  { value: 'America/Anchorage', label: 'Anchorage (Alaska)', region: 'Americas' },
  { value: 'America/Toronto', label: 'Toronto (Eastern)', region: 'Americas' },
  { value: 'America/Vancouver', label: 'Vancouver (Pacific)', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'Mexico City', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'Sao Paulo', region: 'Americas' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'Americas' },

  // Europe
  { value: 'Europe/London', label: 'London (GMT)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid (CET)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome (CET)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)', region: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Istanbul', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens (EET)', region: 'Europe' },

  // Asia & Middle East
  { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'India (IST)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)', region: 'Asia' },
  { value: 'Asia/Yerevan', label: 'Yerevan (Armenia)', region: 'Asia' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem (IST)', region: 'Asia' },
  { value: 'Asia/Tehran', label: 'Tehran (IRST)', region: 'Asia' },

  // Australia & Pacific
  { value: 'Australia/Sydney', label: 'Sydney (AEST)', region: 'Pacific' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST)', region: 'Pacific' },
  { value: 'Australia/Perth', label: 'Perth (AWST)', region: 'Pacific' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST)', region: 'Pacific' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)', region: 'Pacific' },

  // Africa
  { value: 'Africa/Cairo', label: 'Cairo (EET)', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)', region: 'Africa' },

  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', region: 'UTC' },
];

/**
 * Group timezone options by region.
 */
export function getTimezoneOptionsByRegion(): Record<string, typeof TIMEZONE_OPTIONS> {
  const result: Record<string, typeof TIMEZONE_OPTIONS> = {};
  for (const option of TIMEZONE_OPTIONS) {
    const existing = result[option.region];
    if (existing) {
      existing.push(option);
    } else {
      result[option.region] = [option];
    }
  }
  return result;
}
